import { useState } from 'react';
import { gramsToKg, kgToGrams } from '@liftx/core';
import type { SessionSetRecord } from '../../data/types';
import styles from './Workout.module.css';

function parseIntOrNull(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;
  const parsed = Number.parseInt(trimmed, 10);
  return Number.isNaN(parsed) ? null : parsed;
}

/**
 * One set: target hint, weight, reps, and the ✓ control. Master Phase 6.
 *
 * Logging a set is one tap when the pre-fill is right (`PHASE-0-ARCHITECTURE.md`
 * §14) — the row *is* the editor, no modal. `inputmode="decimal"`/`"numeric"`
 * for weight/reps, with a real accessible name combining the set's full state
 * into one label rather than four separately-labelled fields (§16).
 */
export function SetRow({
  set,
  index,
  targetLabel,
  hideWeight = false,
  placeholderWeightKg,
  placeholderReps,
  plannedReps,
  canRemove,
  onComplete,
  onUncomplete,
  onRemove,
}: {
  set: SessionSetRecord;
  index: number;
  targetLabel: string | null;
  /**
   * Omits the weight field entirely for a genuinely bodyweight exercise
   * (Bodyweight Exercises feature, 2026-09-16) — not merely blank or
   * disabled, since there is no weight to enter at all, and a visible-but-
   * unusable field would still read as "why can't I fill this in?" rather
   * than "this doesn't apply here." `onComplete` is unaffected: with the
   * field never rendered, `weightKg` simply never leaves its initial empty
   * string, so `weightG` is `null` on completion exactly like an
   * intentionally-left-blank field already behaves for any other exercise.
   */
  hideWeight?: boolean;
  placeholderWeightKg: number | null;
  placeholderReps: number | null;
  /**
   * The plan's exact rep target (`repMin === repMax`), when there is one —
   * Planned Reps as Editable Default, Phase 2. Unlike `placeholderReps`
   * (previous performance, shown only as a greyed HTML `placeholder` an
   * untouched field never actually submits), this becomes the reps input's
   * real, editable, submittable value: completing the set without editing
   * it records this number, not `null`. `null` for a genuine range, no
   * prescription at all, or no usable target — existing blank-by-default
   * behaviour is unchanged in every one of those cases.
   */
  plannedReps: number | null;
  /** False for the last remaining set — an exercise must always keep at least one. */
  canRemove: boolean;
  onComplete: (values: { weightG: number | null; reps: number | null }) => void;
  onUncomplete: () => void;
  onRemove: () => void;
}): React.JSX.Element {
  const [weightKg, setWeightKg] = useState(set.weightG === null ? '' : String(gramsToKg(set.weightG)));
  // A persisted actual value always wins; otherwise an exact planned target
  // pre-fills the real (not placeholder) value — see `plannedReps`'s own doc.
  const [reps, setReps] = useState(
    set.reps !== null ? String(set.reps) : plannedReps !== null ? String(plannedReps) : '',
  );
  // Local, unpersisted — resets naturally on remount (`ExercisePanel` keys
  // this component by `set.id`) because this is exactly the "has the user
  // touched this input during this mounted row" state the plan explicitly
  // allows rather than a new persistence field purely for styling.
  const [repsTouched, setRepsTouched] = useState(false);
  const showsPlannedReps = set.reps === null && plannedReps !== null && !repsTouched;

  const label = `Set ${String(index + 1)}${targetLabel !== null ? `, ${targetLabel}` : ''}${
    set.isCompleted ? ', completed' : ''
  }`;

  function handleToggle(): void {
    if (set.isCompleted) {
      onUncomplete();
      return;
    }
    onComplete({
      weightG: weightKg.trim().length === 0 ? null : kgToGrams(Number.parseFloat(weightKg)),
      reps: parseIntOrNull(reps),
    });
  }

  return (
    <li
      className={canRemove ? `${styles.setRow} ${styles.setRowRemovable}` : styles.setRow}
      data-completed={set.isCompleted}
      aria-label={label}
    >
      <span className={styles.setNumber} aria-hidden="true">
        {index + 1}
      </span>
      <div className={styles.setFields}>
        {/* A native <label> wrapper, not just a styling <div> — tapping the
            "kg"/"reps" affix still focuses the input, so the full pill stays
            the tap target rather than shrinking to the number alone. The
            input's own aria-label remains its accessible name either way. */}
        {!hideWeight && (
          <>
            <label className={styles.setField}>
              <input
                type="number"
                inputMode="decimal"
                step="0.5"
                className={styles.setInput}
                aria-label={`Set ${String(index + 1)} weight in kilograms`}
                placeholder={placeholderWeightKg === null ? '—' : String(placeholderWeightKg)}
                value={weightKg}
                disabled={set.isCompleted}
                onChange={(event) => setWeightKg(event.target.value)}
              />
              <span className={styles.setFieldUnit} aria-hidden="true">
                kg
              </span>
            </label>
            <span className={styles.setFieldDivider} aria-hidden="true" />
          </>
        )}
        <label className={styles.setField}>
          <input
            type="number"
            inputMode="numeric"
            className={showsPlannedReps ? `${styles.setInput} ${styles.setInputPlanned}` : styles.setInput}
            aria-label={`Set ${String(index + 1)} reps`}
            placeholder={placeholderReps === null ? '—' : String(placeholderReps)}
            value={reps}
            disabled={set.isCompleted}
            onChange={(event) => {
              setReps(event.target.value);
              setRepsTouched(true);
            }}
          />
          <span className={styles.setFieldUnit} aria-hidden="true">
            reps
          </span>
        </label>
      </div>
      <button
        type="button"
        className={set.isCompleted ? `${styles.completeButton} ${styles.completeButtonOn}` : styles.completeButton}
        role="checkbox"
        aria-checked={set.isCompleted}
        aria-label={set.isCompleted ? `Mark set ${String(index + 1)} incomplete` : `Complete set ${String(index + 1)}`}
        onClick={handleToggle}
      >
        {set.isCompleted && (
          <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
            <path
              d="M5.5 11.5l3.5 3.5 7.5-8"
              stroke="currentColor"
              strokeWidth="2.25"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </button>
      {canRemove && (
        <button
          type="button"
          className={styles.removeSetButton}
          aria-label={`Remove set ${String(index + 1)}`}
          onClick={onRemove}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path
              d="M3 4.5h10M6.5 4.5V3a1 1 0 0 1 1-1h1a1 1 0 0 1 1 1v1.5M6.5 7.5v4M9.5 7.5v4M4 4.5l.6 8.1a1 1 0 0 0 1 .9h4.8a1 1 0 0 0 1-.9l.6-8.1"
              stroke="currentColor"
              strokeWidth="1.3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      )}
    </li>
  );
}
